import { booksChanged } from '../lib/changed';
import { supabase } from '../lib/supabase';
import type { NoticeInput } from '../shell/notice';
import { bulkMessage, num } from './model';

// The Queue's changes (0040), each as you, each with Undo in the shared notice. A change says so
// (booksChanged), so the list (and Shelves, under a book's side panel) reads its books again.

async function call<T>(fn: string, args: Record<string, unknown>): Promise<T> {
  if (!supabase) throw new Error('Not connected to a database.');
  const { data, error } = await supabase.rpc(fn, args);
  if (error) throw new Error(error.message);
  return data as T;
}

type Notice = (n: NoticeInput) => void;
const failed = (notice: Notice, what: string, e: unknown) => notice({ message: `${what}: ${e instanceof Error ? e.message : String(e)}`, done: false });

async function undoOne(notice: Notice, action: string, title: string) {
  try {
    const res = await call<{ kept?: string[] } | null>('undo_action', { action });
    booksChanged();
    notice({ message: res?.kept?.length ? `Undone, except what changed since` : `Undone: ${title} is as it was` });
  } catch (e) {
    failed(notice, 'Couldn’t undo', e);
  }
}

async function undoGroup(notice: Notice, group: string) {
  try {
    const res = await call<{ undone: number; kept?: string[] }>('undo_group', { grp: group });
    booksChanged();
    notice({ message: `Undone for ${res.undone === 1 ? '1 book' : `${num(res.undone)} books`}${res.kept?.length ? ', except what changed since' : ''}` });
  } catch (e) {
    failed(notice, 'Couldn’t undo', e);
  }
}

export type Place = { place: 'top' | 'bottom' | number } | { before: string } | { after: string };

/** A new place on the book's own shelf. False when nothing changed or it failed. */
export async function placeBook(notice: Notice, book: { id: string; title: string }, place: Place, words: string): Promise<boolean> {
  try {
    const action = await call<string | null>('queue_place', { book: book.id, place });
    if (!action) return false;
    booksChanged();
    notice({ message: words, undo: () => void undoOne(notice, action, book.title) });
    return true;
  } catch (e) {
    failed(notice, 'Not moved', e);
    booksChanged(); // read the shelf again, so the screen shows where it really is
    return false;
  }
}

/** One book to another shelf (the book page's own book_move). */
export async function moveBook(notice: Notice, book: { id: string; title: string }, to: string, details: Record<string, unknown>, words: string): Promise<boolean> {
  try {
    const action = await call<string>('book_move', { book: book.id, to_shelf: to, details });
    booksChanged();
    notice({ message: words, undo: () => void undoOne(notice, action, book.title) });
    return true;
  } catch (e) {
    failed(notice, 'Not moved', e);
    booksChanged();
    return false;
  }
}

/** One book's Interest or read type, from its row. */
export async function setBook(notice: Notice, book: { id: string; title: string }, field: 'interest' | 'read_type', value: string | number | null, words: string): Promise<boolean> {
  try {
    const action = await call<string | null>('book_set', { book: book.id, field, value: value === null ? null : String(value) });
    booksChanged();
    if (action) notice({ message: words, undo: () => void undoOne(notice, action, book.title) });
    return true;
  } catch (e) {
    failed(notice, 'Not saved', e);
    booksChanged();
    return false;
  }
}

type Bulk = { group: string | null; moved?: number; changed?: number; skipped: { book: string; reason: string }[] };

/** Several books to another shelf, one Undo. */
export async function moveBooks(notice: Notice, ids: string[], to: string, details: Record<string, unknown>, what: string, ask?: { label: string; run: () => void }): Promise<boolean> {
  try {
    const res = await call<Bulk>('books_move', { books: ids, to_shelf: to, details });
    booksChanged();
    const done = res.moved ?? 0;
    notice({
      message: (done ? bulkMessage(done, what, res.skipped.length) : `Nothing moved: ${res.skipped[0]?.reason ?? 'already there'}`) + (done && ask ? '. Should they be Study?' : ''),
      done: done > 0,
      undo: res.group ? () => void undoGroup(notice, res.group!) : undefined,
      action: done ? ask : undefined,
    });
    return done > 0;
  } catch (e) {
    failed(notice, 'Not moved', e);
    booksChanged();
    return false;
  }
}

/** Interest or read type on several books, one Undo. */
export async function setBooks(notice: Notice, ids: string[], field: 'interest' | 'read_type', value: string | number | null, what: string): Promise<boolean> {
  try {
    const res = await call<Bulk>('books_set', { books: ids, field, value: value === null ? null : String(value) });
    booksChanged();
    const done = res.changed ?? 0;
    notice({
      message: done ? bulkMessage(done, what, ids.length - done) : 'Nothing to change: they already have it',
      done: done > 0,
      undo: res.group ? () => void undoGroup(notice, res.group!) : undefined,
    });
    return done > 0;
  } catch (e) {
    failed(notice, 'Not saved', e);
    booksChanged();
    return false;
  }
}

/** Several books to a new place on their shelf, as a block (0041), one Undo. */
export async function placeBooks(notice: Notice, ids: string[], where: 'top' | 'bottom' | number, words: string): Promise<boolean> {
  try {
    const res = await call<Bulk>('books_place', { books: ids, place: { place: where } });
    booksChanged();
    const done = res.moved ?? 0;
    notice({
      message: done ? words : `Nothing moved: ${res.skipped[0]?.reason ?? 'already there'}`,
      done: done > 0,
      undo: res.group ? () => void undoGroup(notice, res.group!) : undefined,
    });
    return done > 0;
  } catch (e) {
    failed(notice, 'Not moved', e);
    booksChanged();
    return false;
  }
}

/**
 * Revisit's Try again on an Abandoned book (Kristi, Oct 30): back to Future reads, where you
 * choose, with ↺ Try again on. Two of the book page's own steps, one Undo that takes back both.
 */
export async function tryAgainBook(notice: Notice, book: { id: string; title: string }, place: 'kept' | 'top' | 'bottom'): Promise<boolean> {
  let moved: string | null = null;
  try {
    moved = await call<string>('book_move', { book: book.id, to_shelf: 'future_reads', details: { place: { place } } });
    const marked = await call<string>('book_mark', { book: book.id, mark: 'try_again', turn_on: true });
    booksChanged();
    notice({
      message: `${book.title} back in Future reads, with ↺ Try again`,
      undo: async () => {
        try {
          const a = await call<{ kept?: string[] } | null>('undo_action', { action: marked });
          const b = await call<{ kept?: string[] } | null>('undo_action', { action: moved });
          booksChanged();
          notice({ message: a?.kept?.length || b?.kept?.length ? 'Undone, except what changed since' : `Undone: ${book.title} is as it was` });
        } catch (e) {
          failed(notice, 'Couldn’t undo', e);
        }
      },
    });
    return true;
  } catch (e) {
    // Moved but not marked: take the move back, so nothing is left half done.
    if (moved) await call('undo_action', { action: moved }).catch(() => null);
    failed(notice, 'Not moved', e);
    booksChanged();
    return false;
  }
}

/**
 * Put them in series order (0056; a series page, Discover Part 2): the series' books on Future
 * reads swap places within the places they already hold. One Undo for them all.
 */
export async function seriesOrder(notice: Notice, series: { id: string; name: string }): Promise<boolean> {
  try {
    const res = await call<{ group: string | null; moved: number }>('series_order', { ser: series.id });
    booksChanged();
    notice({
      message: res.moved ? `${series.name} is in series order in Future reads` : `${series.name} was already in series order`,
      done: res.moved > 0,
      undo: res.group ? () => void undoGroup(notice, res.group!) : undefined,
    });
    return res.moved > 0;
  } catch (e) {
    failed(notice, 'Not put in order', e);
    booksChanged();
    return false;
  }
}
